import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Dialog,
  Typography,
} from "@mui/material";
import { sumBy } from "lodash";
import { ReactElement, useState } from "react";

import {
  CampaignStatus,
  DeleteSuppressionsMutation,
  DeleteSuppressionsMutationVariables,
  SuppressionStage,
} from "~frontend/__generated__/graphql";
import Loading from "~frontend/components/Loading";
import { DELETE_SUPPRESSIONS } from "~frontend/graphql/campaigns";
import { CampaignWithSuppressionData } from "~frontend/routes/types";
import theme, { palette } from "~frontend/theme";
import { jobInProgress } from "~frontend/util/jobs";
import useMutation from "~frontend/util/useMutation";

import SuppressionCsvUpload from "./SuppressionCsvUpload";
import SuppressionsTable from "./SuppressionsTable";
import { Suppression } from "./types";

export default function SuppressionList({
  campaign,
  suppressions,
  campaignStartedOrStarting,
  stage,
  expanded,
  onToggleExpanded,
  onApplySuppressions,
}: {
  campaign: CampaignWithSuppressionData;
  suppressions: Suppression[];
  campaignStartedOrStarting: boolean;
  stage: SuppressionStage;
  expanded: boolean;
  onToggleExpanded: () => void;
  onApplySuppressions: () => void;
}): ReactElement {
  const [deleteSuppressions] = useMutation<
    DeleteSuppressionsMutation,
    DeleteSuppressionsMutationVariables
  >(DELETE_SUPPRESSIONS, {
    onErrorSnackbarMessage: "Failed to delete suppressions",
  });

  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  async function handleRemove(selections: {
    overlapIds: string[];
    listIds: string[];
    shortcodeSubscriberSuppressionIds: string[];
    audienceSuppressionIds: string[];
  }) {
    await deleteSuppressions({
      variables: {
        input: {
          campaignId: campaign.id,
          overlapIds: selections.overlapIds,
          listIds: selections.listIds,
          shortcodeSubscriberSuppressionIds:
            selections.shortcodeSubscriberSuppressionIds,
          audienceSuppressionIds: selections.audienceSuppressionIds,
        },
      },
    });
  }

  async function handleUpload() {
    setUploadModalOpen(true);
  }

  function renderTableOrLoading(): ReactElement {
    const jobChecks = [
      {
        job: campaign.createSuppressionsFromListJob,
        message: "Suppressions are being created from an uploaded list",
      },
      {
        job: campaign.createSuppressionsFromCampaignOverlapsJob,
        message: "Suppressions are being created from overlapping campaigns",
      },
      {
        job: campaign.createSuppressionsFromShortcodeSubscribersJob,
        message: "Suppressions are being created from short code subscribers",
      },
      {
        job: campaign.createSuppressionsFromAudiencesJob,
        message: "Suppressions are being created from audiences",
      },
      {
        job: campaign.deleteSuppressionsJob,
        message: "Suppressions are being deleted",
      },
    ];

    const activeJob = jobChecks.find(({ job }) => job && jobInProgress(job));

    if (activeJob) {
      return (
        <Box sx={{ margin: 4 }}>
          <Loading />
          <Box sx={{ textAlign: "center" }}>{activeJob.message}</Box>
        </Box>
      );
    }

    return (
      <Accordion
        disableGutters
        expanded={expanded}
        onChange={() => onToggleExpanded()}
        sx={{ padding: 0, background: "none" }}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMore sx={{ marginY: "-4.5px", fontSize: "1.2em" }} />
          }
          sx={{
            backgroundColor: palette.midGray,
            minHeight: "27px",
            ".MuiAccordionSummary-content": { margin: 0 },
          }}
        >
          <Typography variant="body2Bold">
            {stage === SuppressionStage.PostStart
              ? "Post-Launch Suppressions"
              : campaign.status === CampaignStatus.Draft
                ? "Your Suppressions"
                : "Pre-Launch Suppressions"}
          </Typography>
          {expanded ? null : (
            <Typography
              variant="body2"
              sx={{ paddingLeft: theme.spacing(0.5) }}
            >
              ({sumBy(suppressions, "count").toLocaleString()})
            </Typography>
          )}
        </AccordionSummary>
        <AccordionDetails
          sx={{
            padding: "0 24px 24px 24px",
            borderStyle: "solid",
            borderWidth: "1px",
            borderColor: palette.midGray,
          }}
        >
          <SuppressionsTable
            campaign={campaign}
            suppressions={suppressions}
            stage={stage}
            onRemove={handleRemove}
            onUpload={handleUpload}
            readOnly={
              stage === SuppressionStage.PreStart && campaignStartedOrStarting
            }
          />
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Box>
      {renderTableOrLoading()}
      <Dialog open={uploadModalOpen} fullWidth maxWidth="md">
        {uploadModalOpen && (
          <SuppressionCsvUpload
            campaign={campaign}
            onCloseModal={() => setUploadModalOpen(false)}
            onApplySuppressions={onApplySuppressions}
          />
        )}
      </Dialog>
    </Box>
  );
}
